import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react'
import {
  MOBILE_CAPTURE_AUDIO_ACCEPT,
  uploadMobileCaptureMedia,
  type MobileCaptureUploadMethod,
} from '../mobileCaptureApi'
import './MobileCapturePage.css'

type Props = {
  workspaceName: string
  getAccessToken: () => string | null
  onAccessToken: (token: string) => void
  onAuthExpired: () => void
  onLogout: () => void
}

type RecordPhase = 'ready' | 'recording' | 'recorded' | 'uploading' | 'success' | 'error'
type FilePhase = 'idle' | 'preview' | 'uploading' | 'success' | 'error'

const RECORDER_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/mp4',
  'audio/webm',
  'audio/ogg;codecs=opus',
]

function pickRecorderMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined
  for (const mime of RECORDER_MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(mime)) return mime
  }
  return undefined
}

function mimeToExtension(mime: string): string {
  if (mime.includes('mp4') || mime.includes('m4a')) return 'm4a'
  if (mime.includes('ogg')) return 'ogg'
  if (mime.includes('wav')) return 'wav'
  return 'webm'
}

function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '—'
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function MobileAudioCapturePage({
  workspaceName,
  getAccessToken,
  onAccessToken: _onAccessToken,
  onAuthExpired,
  onLogout,
}: Props) {
  const recorderMime = pickRecorderMime()
  const recorderSupported = Boolean(recorderMime && navigator.mediaDevices?.getUserMedia)

  const [recordPhase, setRecordPhase] = useState<RecordPhase>('ready')
  const [filePhase, setFilePhase] = useState<FilePhase>('idle')
  const [recordSeconds, setRecordSeconds] = useState(0)
  const [recordError, setRecordError] = useState<string | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [permissionDenied, setPermissionDenied] = useState(false)
  const [recordBlob, setRecordBlob] = useState<Blob | null>(null)
  const [recordPreviewUrl, setRecordPreviewUrl] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [toast, setToast] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const recordChunksRef = useRef<BlobPart[]>([])
  const recordTimerRef = useRef<number | null>(null)
  const recordStartedAtRef = useRef<number>(0)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const recordAudioRef = useRef<HTMLAudioElement | null>(null)
  const fileAudioRef = useRef<HTMLAudioElement | null>(null)
  const toastTimerRef = useRef<number | undefined>(undefined)

  const showToast = (msg: string) => {
    setToast(msg)
    if (toastTimerRef.current !== undefined) window.clearTimeout(toastTimerRef.current)
    toastTimerRef.current = window.setTimeout(() => setToast(null), 2200)
  }

  const cleanupStream = useCallback(() => {
    mediaRecorderRef.current = null
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop())
    mediaStreamRef.current = null
    if (recordTimerRef.current !== null) {
      window.clearInterval(recordTimerRef.current)
      recordTimerRef.current = null
    }
  }, [])

  const revokePreview = useCallback((url: string | null) => {
    if (url && url.startsWith('blob:')) URL.revokeObjectURL(url)
  }, [])

  useEffect(() => {
    return () => {
      cleanupStream()
      revokePreview(recordPreviewUrl)
      revokePreview(filePreviewUrl)
      if (toastTimerRef.current !== undefined) window.clearTimeout(toastTimerRef.current)
    }
  }, [cleanupStream, filePreviewUrl, recordPreviewUrl, revokePreview])

  const resetRecording = useCallback(() => {
    cleanupStream()
    revokePreview(recordPreviewUrl)
    setRecordPreviewUrl(null)
    setRecordBlob(null)
    setRecordSeconds(0)
    setRecordError(null)
    setRecordPhase('ready')
    setPermissionDenied(false)
  }, [cleanupStream, recordPreviewUrl, revokePreview])

  const resetFile = useCallback(() => {
    revokePreview(filePreviewUrl)
    setFilePreviewUrl(null)
    setSelectedFile(null)
    setFileError(null)
    setFilePhase('idle')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [filePreviewUrl, revokePreview])

  const startRecording = async () => {
    if (!recorderSupported || recordPhase === 'uploading') return
    setRecordError(null)
    setPermissionDenied(false)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaStreamRef.current = stream
      const recorder = new MediaRecorder(stream, recorderMime ? { mimeType: recorderMime } : undefined)
      recordChunksRef.current = []
      recorder.ondataavailable = (ev) => {
        if (ev.data.size > 0) recordChunksRef.current.push(ev.data)
      }
      recorder.onstop = () => {
        const mime = recorder.mimeType || recorderMime || 'audio/webm'
        const blob = new Blob(recordChunksRef.current, { type: mime })
        setRecordBlob(blob)
        revokePreview(recordPreviewUrl)
        const url = URL.createObjectURL(blob)
        setRecordPreviewUrl(url)
        setRecordPhase('recorded')
        cleanupStream()
      }
      recorder.onerror = () => {
        setRecordError('录音失败，请重试')
        setRecordPhase('error')
        cleanupStream()
      }
      mediaRecorderRef.current = recorder
      recorder.start()
      recordStartedAtRef.current = Date.now()
      setRecordSeconds(0)
      setRecordPhase('recording')
      recordTimerRef.current = window.setInterval(() => {
        setRecordSeconds(Math.floor((Date.now() - recordStartedAtRef.current) / 1000))
      }, 250)
    } catch (err) {
      const name = (err as DOMException).name
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setPermissionDenied(true)
        setRecordError('麦克风权限未开启。请在系统设置中允许浏览器使用麦克风后重试。')
      } else {
        setRecordError((err as Error).message || '无法开始录音')
      }
      setRecordPhase('error')
      cleanupStream()
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop()
    }
  }

  const uploadBlob = async (file: File, captureMethod: MobileCaptureUploadMethod) => {
    const token = getAccessToken()
    if (!token) {
      onAuthExpired()
      return
    }
    setUploadProgress(0)
    try {
      await uploadMobileCaptureMedia(token, file, {
        captureMethod,
        onProgress: setUploadProgress,
      })
      showToast('上传成功')
      if (captureMethod === 'recording') {
        setRecordPhase('success')
      } else {
        setFilePhase('success')
      }
    } catch (err) {
      const message = (err as Error).message || '上传失败'
      if (captureMethod === 'recording') {
        setRecordError(message)
        setRecordPhase('error')
      } else {
        setFileError(message)
        setFilePhase('error')
      }
    }
  }

  const useRecording = async () => {
    if (!recordBlob) return
    const mime = recordBlob.type || recorderMime || 'audio/webm'
    const ext = mimeToExtension(mime)
    const file = new File([recordBlob], `mobile-recording-${Date.now()}.${ext}`, { type: mime })
    setRecordPhase('uploading')
    await uploadBlob(file, 'recording')
  }

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('audio/') && !/\.(wav|mp3|m4a|aac|ogg|flac|webm|opus)$/i.test(file.name)) {
      setFileError('不支持的音频格式')
      setFilePhase('error')
      return
    }
    revokePreview(filePreviewUrl)
    setSelectedFile(file)
    setFilePreviewUrl(URL.createObjectURL(file))
    setFileError(null)
    setFilePhase('preview')
  }

  const uploadSelectedFile = async () => {
    if (!selectedFile) return
    setFilePhase('uploading')
    await uploadBlob(selectedFile, 'file')
  }

  const busy = recordPhase === 'uploading' || filePhase === 'uploading'

  return (
    <div className="mc-page mc-audio-page">
      <header className="mc-header">
        <div className="mc-brand">VISTOERA</div>
        <h1>上传音频</h1>
        <p className="mc-subtitle">
          {workspaceName || '当前工作区'}
          <br />
          录制音频或从手机选择音频文件上传
        </p>
        <button type="button" className="mc-logout-link" onClick={onLogout}>
          退出登录
        </button>
      </header>

      {toast && <div className="mc-banner mc-banner-info">{toast}</div>}

      <div className="mc-audio-entry" data-testid="mc-audio-entry">
        <button
          type="button"
          className="mc-audio-card"
          data-testid="mc-record-audio-btn"
          disabled={busy || !recorderSupported}
          onClick={() => {
            resetFile()
            if (recordPhase === 'ready' || recordPhase === 'error') void startRecording()
          }}
        >
          <span className="mc-audio-card-icon" aria-hidden="true">🎙</span>
          <span className="mc-audio-card-label">录制音频</span>
          {!recorderSupported ? (
            <span className="mc-audio-card-hint">当前浏览器不支持录音</span>
          ) : null}
        </button>

        <button
          type="button"
          className="mc-audio-card"
          data-testid="mc-select-phone-audio-btn"
          disabled={busy}
          onClick={() => {
            resetRecording()
            fileInputRef.current?.click()
          }}
        >
          <span className="mc-audio-card-icon" aria-hidden="true">🎵</span>
          <span className="mc-audio-card-label">选择手机音频</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={MOBILE_CAPTURE_AUDIO_ACCEPT}
          className="mc-hidden-file"
          data-testid="mc-audio-file-input"
          onChange={onFileChange}
        />
      </div>

      {(recordPhase === 'recording' || recordPhase === 'recorded' || recordPhase === 'uploading' || recordPhase === 'error' || recordPhase === 'success') && (
        <section className="mc-section" data-testid="mc-audio-recording-panel">
          <h2 className="mc-section-title">录制音频</h2>
          {permissionDenied ? (
            <div className="mc-banner mc-banner-error" data-testid="mc-mic-permission-denied">
              {recordError}
            </div>
          ) : null}
          {recordPhase === 'recording' ? (
            <>
              <p className="mc-audio-status">录制中 · {formatDuration(recordSeconds)}</p>
              <button
                type="button"
                className="mc-big-button mc-primary"
                data-testid="mc-stop-recording-btn"
                onClick={stopRecording}
              >
                停止录制
              </button>
            </>
          ) : null}
          {recordPhase === 'recorded' || recordPhase === 'uploading' || recordPhase === 'error' ? (
            <>
              {recordPreviewUrl ? (
                <audio
                  ref={recordAudioRef}
                  src={recordPreviewUrl}
                  controls
                  preload="metadata"
                  className="mc-audio-preview"
                  data-testid="mc-recording-preview"
                />
              ) : null}
              {recordPhase === 'uploading' ? (
                <p className="mc-audio-status">上传中{uploadProgress > 0 ? ` ${uploadProgress}%` : '…'}</p>
              ) : null}
              {recordError ? <div className="mc-banner mc-banner-error">{recordError}</div> : null}
              <div className="mc-audio-actions">
                <button
                  type="button"
                  className="mc-big-button mc-secondary"
                  disabled={recordPhase === 'uploading'}
                  data-testid="mc-rerecord-btn"
                  onClick={resetRecording}
                >
                  重新录制
                </button>
                <button
                  type="button"
                  className="mc-big-button mc-primary"
                  disabled={recordPhase === 'uploading' || !recordBlob}
                  data-testid="mc-use-recording-btn"
                  onClick={() => void useRecording()}
                >
                  使用此录音
                </button>
              </div>
            </>
          ) : null}
          {recordPhase === 'success' ? (
            <div className="mc-banner mc-banner-info" data-testid="mc-recording-upload-success">
              录音已上传。可返回电脑端选择该音频资产。
            </div>
          ) : null}
        </section>
      )}

      {(filePhase === 'preview' || filePhase === 'uploading' || filePhase === 'error' || filePhase === 'success') && selectedFile ? (
        <section className="mc-section" data-testid="mc-audio-file-panel">
          <h2 className="mc-section-title">选择手机音频</h2>
          <div className="mc-audio-meta">
            <div>{selectedFile.name}</div>
            <div>{formatBytes(selectedFile.size)}</div>
          </div>
          {filePreviewUrl ? (
            <audio
              ref={fileAudioRef}
              src={filePreviewUrl}
              controls
              preload="metadata"
              className="mc-audio-preview"
              data-testid="mc-file-preview"
            />
          ) : null}
          {filePhase === 'uploading' ? (
            <p className="mc-audio-status">上传中{uploadProgress > 0 ? ` ${uploadProgress}%` : '…'}</p>
          ) : null}
          {fileError ? <div className="mc-banner mc-banner-error">{fileError}</div> : null}
          <div className="mc-audio-actions">
            <button
              type="button"
              className="mc-big-button mc-secondary"
              disabled={filePhase === 'uploading'}
              onClick={resetFile}
            >
              重新选择
            </button>
            <button
              type="button"
              className="mc-big-button mc-primary"
              disabled={filePhase === 'uploading'}
              data-testid="mc-upload-file-btn"
              onClick={() => void uploadSelectedFile()}
            >
              确认上传
            </button>
          </div>
          {filePhase === 'success' ? (
            <div className="mc-banner mc-banner-info" data-testid="mc-file-upload-success">
              音频已上传。可返回电脑端选择该音频资产。
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  )
}
